import {
  IBatchConfirmUploadRequest,
  IBatchConfirmUploadResponse,
  IBatchRequestUploadUrlRequest,
  IBatchRequestUploadUrlResponse,
  IRoundDocumentConfirmRequest,
  IRoundDocumentDeleteResponse,
  IRoundDocumentDownloadUrlResponse,
  IRoundDocumentMetadata,
  IRoundDocumentUploadUrlRequest,
  IRoundDocumentUploadUrlResponse,
  RoundDocumentType,
} from '../../data-models/round-document.data-model';
import { MaggieHttp } from '../MaggieHttp';

/**
 * Fetch all documents for a specific round
 * @param roundId - The round ID
 * @returns Array of document metadata
 */
export async function getRoundDocuments(roundId: number): Promise<IRoundDocumentMetadata[]> {
  const response = await MaggieHttp.maggie.get<IRoundDocumentMetadata[]>(`/round/${roundId}/documents`);
  return response.data;
}

/**
 * Request a presigned S3 upload URL for a document
 * @param roundId - The round ID
 * @param payload - Upload URL request payload
 * @returns Upload URL response with fileId, uploadUrl, and s3Key
 */
export async function requestRoundDocumentUploadUrl(
  roundId: number,
  payload: IRoundDocumentUploadUrlRequest
): Promise<IRoundDocumentUploadUrlResponse> {
  const response = await MaggieHttp.maggie.post<
    IRoundDocumentUploadUrlRequest,
    IRoundDocumentUploadUrlResponse
  >(`/round/${roundId}/documents/upload-url`, payload);
  return response.data;
}

/**
 * Confirm a document upload and save metadata to database
 * @param roundId - The round ID
 * @param payload - Confirmation request payload
 * @returns Document metadata
 */
export async function confirmRoundDocumentUpload(
  roundId: number,
  payload: IRoundDocumentConfirmRequest
): Promise<IRoundDocumentMetadata> {
  const response = await MaggieHttp.maggie.post<IRoundDocumentConfirmRequest, IRoundDocumentMetadata>(
    `/round/${roundId}/documents/confirm`,
    payload
  );
  return response.data;
}

/**
 * Get a presigned download URL for a document
 * @param roundId - The round ID
 * @param documentType - Type of document (captable or closing_doc)
 * @returns Download URL response
 */
export async function getRoundDocumentDownloadUrl(
  roundId: number,
  documentType: RoundDocumentType
): Promise<IRoundDocumentDownloadUrlResponse> {
  const response = await MaggieHttp.maggie.get<IRoundDocumentDownloadUrlResponse>(
    `/round/${roundId}/documents/${documentType}/download`
  );
  return response.data;
}

/**
 * Get a presigned download URL for a specific document by ID
 * @param roundId - The round ID
 * @param documentId - The document ID
 * @returns Download URL response
 */
export async function getRoundDocumentDownloadUrlById(
  roundId: number,
  documentId: number
): Promise<IRoundDocumentDownloadUrlResponse> {
  const response = await MaggieHttp.maggie.get<IRoundDocumentDownloadUrlResponse>(
    `/round/${roundId}/documents/${documentId}/download`
  );
  return response.data;
}

/**
 * Delete a document (soft delete)
 * @param roundId - The round ID
 * @param documentType - Type of document to delete
 * @returns Delete confirmation response
 */
export async function deleteRoundDocument(
  roundId: number,
  documentType: RoundDocumentType
): Promise<IRoundDocumentDeleteResponse> {
  const response = await MaggieHttp.maggie.delete<IRoundDocumentDeleteResponse>(
    `/round/${roundId}/documents/${documentType}`
  );
  return response.data;
}

/**
 * Delete a specific document by ID (soft delete)
 * @param roundId - The round ID
 * @param documentId - The document ID to delete
 * @returns Delete confirmation response
 */
export async function deleteRoundDocumentById(
  roundId: number,
  documentId: number
): Promise<IRoundDocumentDeleteResponse> {
  const response = await MaggieHttp.maggie.delete<IRoundDocumentDeleteResponse>(
    `/round/${roundId}/documents/${documentId}`
  );
  return response.data;
}

/**
 * Request presigned S3 upload URLs for multiple documents
 * @param roundId - The round ID
 * @param payload - Batch upload URL request payload
 * @returns Array of upload URL responses with fileId, uploadUrl, and s3Key
 */
export async function requestBatchRoundDocumentUploadUrls(
  roundId: number,
  payload: IBatchRequestUploadUrlRequest
): Promise<IBatchRequestUploadUrlResponse[]> {
  const response = await MaggieHttp.maggie.post<
    IBatchRequestUploadUrlRequest,
    IBatchRequestUploadUrlResponse[]
  >(`/round/${roundId}/documents/upload-urls`, payload);
  return response.data;
}

/**
 * Confirm multiple document uploads and save metadata to database
 * @param roundId - The round ID
 * @param payload - Batch confirmation request payload
 * @returns Batch upload result with success and error arrays
 */
export async function confirmBatchRoundDocumentUpload(
  roundId: number,
  payload: IBatchConfirmUploadRequest
): Promise<IBatchConfirmUploadResponse> {
  const response = await MaggieHttp.maggie.post<IBatchConfirmUploadRequest, IBatchConfirmUploadResponse>(
    `/round/${roundId}/documents/confirm-batch`,
    payload
  );
  return response.data;
}
